/**
 * HomePage section sub-components.
 *
 * Extracted from HomePage.jsx (was 428 lines, code review flagged >300 limit).
 * Each section is fully static (no state, no effects) so it can render entirely
 * from props/data imports — perfect for SSG.
 */
import { Link } from "react-router-dom";
import { TOP_REVIEWS, AGGREGATE } from "../../data/reviews";

/* Markdown-lite inline renderer (subset used in FAQ answers : **bold** + [label](/url)). */
export function renderFaqInline(text) {
    const parts = [];
    const re = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g;
    let last = 0;
    let m;
    let key = 0;
    while ((m = re.exec(text)) !== null) {
        if (m.index > last) parts.push(text.slice(last, m.index));
        const tk = m[0];
        if (tk.startsWith("**")) {
            parts.push(<strong key={key++}>{tk.slice(2, -2)}</strong>);
        } else {
            const lm = /\[([^\]]+)\]\(([^)]+)\)/.exec(tk);
            parts.push(
                <Link key={key++} to={lm[2]} className="text-[var(--navy)] underline decoration-[var(--amber)] decoration-2 underline-offset-2 font-medium">
                    {lm[1]}
                </Link>
            );
        }
        last = re.lastIndex;
    }
    if (last < text.length) parts.push(text.slice(last));
    return parts;
}

/* ───────────────── REVIEWS SECTION ───────────────── */
export function ReviewsSection() {
    return (
        <section className="bg-[var(--paper)] border-y border-[var(--line)] mt-16" aria-labelledby="reviews-h2">
            <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20">
                <div className="grid lg:grid-cols-12 gap-10 items-end">
                    <div className="lg:col-span-7">
                        <div className="divider-amber mb-5" />
                        <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--navy)]/80">
                            <i className="fa-solid fa-star text-[var(--amber)]" /> Avis clients vérifiés
                        </p>
                        <h2 id="reviews-h2" className="heading text-3xl lg:text-4xl font-extrabold mt-3">Nos meilleurs avis clients</h2>
                        <p className="mt-3 text-[var(--ink-soft)] max-w-2xl">
                            Sélection des retours les plus représentatifs de notre travail — interventions récentes à Paris et en Île-de-France.
                        </p>
                    </div>
                    <div className="lg:col-span-5 flex lg:justify-end">
                        <div className="inline-flex items-center gap-4 bg-white border border-[var(--line)] rounded-2xl px-6 py-4">
                            <div>
                                <p className="heading text-4xl font-extrabold text-[var(--navy)]" data-testid="aggregate-rating-value">
                                    {AGGREGATE.ratingValue}<span className="text-lg text-[var(--ink-soft)]">/5</span>
                                </p>
                                <div className="flex gap-1 mt-1 text-[var(--amber)]">
                                    {[0, 1, 2, 3, 4].map((s) => <i key={`agg-star-${s}`} className="fa-solid fa-star text-sm" />)}
                                </div>
                            </div>
                            <div className="border-l border-[var(--line)] pl-4">
                                <p className="heading text-sm font-bold text-[var(--navy)]">{AGGREGATE.reviewCount} avis</p>
                                <p className="text-xs text-[var(--ink-soft)]">clients en IDF</p>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
                    {TOP_REVIEWS.map((r, i) => (
                        <article key={`${r.name}-${r.date}`} className="card-soft p-5 flex flex-col" data-testid={`top-review-${i}`}>
                            <div className="flex items-center justify-between">
                                <div className="flex gap-1 text-[var(--amber)]">
                                    {[0, 1, 2, 3, 4].map((s) => <i key={`star-${s}`} className="fa-solid fa-star text-sm" />)}
                                </div>
                                <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[var(--navy)]/60 bg-[var(--paper)] border border-[var(--line)] rounded-full px-2 py-0.5">{r.service}</span>
                            </div>
                            <p className="mt-4 text-sm text-[var(--ink-soft)] leading-relaxed flex-1">« {r.text} »</p>
                            <div className="mt-4 pt-4 border-t border-[var(--line)] flex items-center justify-between">
                                <div>
                                    <p className="heading font-bold text-sm text-[var(--navy)]">{r.name}</p>
                                    <p className="text-xs text-[var(--ink-soft)]">{r.city}</p>
                                </div>
                                <p className="text-xs text-[var(--ink-soft)]/70">{new Date(r.date).toLocaleDateString("fr-FR", { month: "short", year: "numeric" })}</p>
                            </div>
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ───────────────── VOICE FAQ ───────────────── */
export function VoiceFaqSection({ items }) {
    return (
        <section className="max-w-4xl mx-auto px-5 lg:px-10 pt-16" aria-labelledby="voice-faq-h2">
            <div className="divider-amber mb-5" />
            <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--navy)]/80">
                <i className="fa-solid fa-microphone-lines text-[var(--amber)]" /> Réponses rapides
            </p>
            <h2 id="voice-faq-h2" className="heading text-3xl lg:text-4xl font-extrabold mt-3">L&apos;essentiel en 30 secondes</h2>
            <p className="mt-3 text-[var(--ink-soft)]">Les questions qu&apos;on nous pose chaque jour — version courte.</p>
            <div className="speakable-voice-faq mt-8 grid sm:grid-cols-2 gap-4" data-speakable="true">
                {items.map((f, i) => (
                    <article key={f.q} className="card-soft p-5" data-testid={`voice-faq-${i}`}>
                        <p className="heading font-bold text-[var(--navy)] flex items-start gap-2">
                            <i className="fa-solid fa-circle-question text-[var(--amber)] mt-1 shrink-0" />
                            <span>{f.q}</span>
                        </p>
                        <p className="mt-2 text-sm text-[var(--ink-soft)] leading-relaxed">{f.a}</p>
                    </article>
                ))}
            </div>
        </section>
    );
}

/* ───────────────── MAIN FAQ ───────────────── */
export function MainFaqSection({ items }) {
    return (
        <section className="bg-[var(--paper)] border-y border-[var(--line)] mt-16" aria-labelledby="faq-h2">
            <div className="max-w-4xl mx-auto px-5 lg:px-10 py-20">
                <div className="divider-amber mb-5" />
                <h2 id="faq-h2" className="heading text-3xl lg:text-4xl font-extrabold">Questions fréquentes</h2>
                <p className="mt-4 text-[var(--ink-soft)]">Tout ce que vous voulez savoir avant de nous appeler.</p>
                <div className="mt-8 divide-y divide-[var(--line)] border-y border-[var(--line)] bg-white rounded-2xl">
                    {items.map((f, i) => (
                        <details key={f.q} className="group p-5" data-testid={`faq-item-${i}`}>
                            <summary className="flex items-center justify-between cursor-pointer list-none">
                                <span className="heading font-bold text-[var(--navy)] pr-4">{f.q}</span>
                                <i className="fa-solid fa-plus text-[var(--amber)] group-open:rotate-45 transition-transform" />
                            </summary>
                            <p className="mt-3 text-[var(--ink-soft)] leading-relaxed">{renderFaqInline(f.a)}</p>
                        </details>
                    ))}
                </div>
                <p className="mt-6 text-sm text-[var(--ink-soft)]">
                    Plus de 25 questions détaillées sur notre{" "}
                    <Link to="/faq" className="text-[var(--navy)] underline decoration-[var(--amber)] decoration-2 underline-offset-2 font-semibold">
                        page FAQ complète
                    </Link>.
                </p>
            </div>
        </section>
    );
}
